'use client';

import { GlobalScrollWheel } from '@loamify/ui';
import { SentryClientInit } from '@loamify/observability/react';
import { identityScopedQueryHash } from '@loamify/shared-utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { configureLoamifyApiClient } from '@loamify/shared-utils';
import { FeatureClientPlatform } from '@loamify/feature-flags';
import { FeatureFlagsProvider } from '@/lib/feature-flags-provider';
import { useAuth } from '@/lib/auth-store';
function AuthHydrate({ children }: { children: React.ReactNode }) {
  const bootstrapHydration = useAuth((s) => s.bootstrapHydration);
  useEffect(() => {
    void bootstrapHydration();
  }, [bootstrapHydration]);
  return <>{children}</>;
}

export function Providers({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    configureLoamifyApiClient({ clientApp: FeatureClientPlatform.WEB_ADMIN });
  }, []);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            queryKeyHashFn: (queryKey) =>
              identityScopedQueryHash(
                queryKey,
                useAuth.getState().user?.id,
                (key) => key[0] === 'platform-features',
              ),
          },
        },
      }),
  );

  useEffect(() => {
    let previousUserId = useAuth.getState().user?.id ?? null;
    return useAuth.subscribe((state) => {
      const nextUserId = state.user?.id ?? null;
      if (previousUserId !== nextUserId) {
        void queryClient.cancelQueries({
          predicate: (query) => query.queryKey[0] !== 'platform-features',
        });
        queryClient.removeQueries({
          predicate: (query) => query.queryKey[0] !== 'platform-features',
        });
      }
      previousUserId = nextUserId;
    });
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <SentryClientInit />
      <FeatureFlagsProvider>
        <GlobalScrollWheel />
        <AuthHydrate>{children}</AuthHydrate>
      </FeatureFlagsProvider>
    </QueryClientProvider>
  );
}
